import React, {useEffect, useState} from 'react';
import {Toast} from 'antd-mobile';
import {RightOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {CartIcon, TransferInIcon, TransferOutIcon, WarehouseIcon} from '../../assets/icons';
import DeliveryLayout, { useDeliveryLayout } from './layouts/DeliveryLayout';
import {useDeliveryTaskStats} from "./task/hooks/useDeliveryTaskStats";
import {TaskPath} from "../../enums/DeliveryNavigatePathEnum";
import {useHistoryControl} from '../../hooks/useHistoryControl';
import {preload, useDeliveryTaskOrderList} from './store/deliveryStore';
import {Loading} from '../../components/Loading';
import {useNetwork} from "../../hooks/useNetwork";
import {showNetworkErrorModal} from "../../components/NetworkErrorModal";
import PullToRefreshWrapper from '../../components/PullToRefreshWrapper';
import {t} from "../../i18n";

interface TaskPageProps {
}

const TaskPage: React.FC<TaskPageProps> = () => {
    const navigate = useNavigate();
    const {deliveryTaskOrderList, loading, refreshDeliveryTaskOrderList} = useDeliveryTaskOrderList();
    const taskInfo = useDeliveryTaskStats(deliveryTaskOrderList);
    const {isOnline} = useNetwork();
    const [refreshing, setRefreshing] = useState(false);
    const { podAlertHeight } = useDeliveryLayout();

    useHistoryControl(TaskPath.DELIVERY_TASK, {
        useReplace: true,
        controlNavButton: false
    });

    useEffect(() => {
        refreshDeliveryTaskOrderList();
    }, []);

    useEffect(() => {
        window.history.pushState(null, '', window.location.href);
        window.history.pushState(null, '', window.location.href);

        const handlePopState = () => {
            window.history.pushState(null, '', window.location.href);
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    const handleRefresh = async () => {
        if (!isOnline) {
            Toast.show({
                content: 'Network connection unavailable',
                position: 'bottom'
            });
            return;
        }

        setRefreshing(true);
        try {
            await refreshDeliveryTaskOrderList();
        } catch (error) {
            Toast.show({
                content: 'Refresh failed, please try again later',
                position: 'bottom'
            });
        } finally {
            setRefreshing(false);
        }
    };

    if (loading && !refreshing) {
        return (
            <DeliveryLayout title="Task">
                <Loading count={3}/>
            </DeliveryLayout>
        );
    }

    const handleNewAssignments = () => {
        if (isOnline) {
            navigate(TaskPath.DELIVERY_TASK_ASSIGNMENTS);
            return;
        }
        showNetworkErrorModal();
    };

    const handleTransferIn = () => {
        if (isOnline) {
            navigate(TaskPath.DELIVERY_TASK_TRANSFER_IN);
            return;
        }
        showNetworkErrorModal();
    };

    return (
        <DeliveryLayout title={`${t('pages.delivery.task.title')}`}>
            <div className="h-full overflow-y-auto" style={{ paddingBottom: `calc(80px + ${podAlertHeight}px + env(safe-area-inset-bottom))` }}>
                <PullToRefreshWrapper onRefresh={handleRefresh}>
                    <div className="p-2 flex flex-col gap-2 text-gray-500">
                    <div className="mb-2">{t('pages.delivery.task.deliveryTitle')}</div>
                    {/* 取件扫描卡片 */}
                    <div className="flex bg-white rounded-lg p-4 items-center justify-between"
                         onClick={handleNewAssignments}>
                        <div className="flex w-2/5 h-full p-6">
                            <CartIcon className="w-full h-full"/>
                        </div>
                        <div className="w-2/5 h-full flex-col gap-2">
                            <div className="font-medium text-lg text-black">{t('pages.delivery.task.newTitle')}</div>
                            <div className="flex">
                                <span className="mr-1 text-violet-600">{t('pages.scan')}</span>
                                <RightOutlined color="#1677ff"/>
                            </div>
                        </div>
                        <div className="w-1/5 text-4xl font-bold text-center">{taskInfo?.newCount}</div>
                    </div>

                    {/* 875: 转入合并到新分配入口，隐藏转入入口 */}
                    {/* <div className="flex bg-white rounded-lg p-4 items-center justify-between"
                         onClick={handleTransferIn}>
                        <div className="w-2/5 h-full flex justify-center p-6">
                            <TransferInIcon className="w-full h-full"/>
                        </div>
                        <div className="flex-col w-2/5 h-full gap-2">
                            <div
                                className="font-medium text-lg text-black">{t('pages.delivery.task.transferInTitle')}</div>
                            <span>{t('pages.delivery.task.transferInDesc')}</span>
                            <div className="flex">
                                <span className="mr-1 text-violet-600">{t('pages.scan')}</span>
                                <RightOutlined color="#1677ff"/>
                            </div>
                        </div>
                        <div className="w-1/5 text-4xl font-bold text-center">{taskInfo?.transferInCount}</div>
                    </div> */}

                    <div className="mb-2">{t('pages.delivery.task.returnTitle')}</div>

                    {/* 返回WHS扫描卡片 */}
                    <div className="flex bg-white rounded-lg p-4 items-center justify-between"
                         onClick={() => navigate(TaskPath.DELIVERY_TASK_RETURN)}>
                        <div className="w-1/5 text-4xl font-bold text-center">{taskInfo?.returnCount}</div>
                        <div className="w-2/5 h-full flex-col gap-2">
                            <div
                                className="font-medium text-lg text-black">{t('pages.delivery.task.warehouseTitle')}</div>
                            <div className="flex">
                                <span className="mr-1 text-violet-600">{t('pages.view')}</span>
                                <RightOutlined color="#1677ff"/>
                            </div>
                        </div>

                        <div className="w-2/5 h-full flex justify-center p-6">
                            <WarehouseIcon className="w-full h-full"/>
                        </div>
                    </div>

                    {/* 875: 转出入口隐藏 */}
                    {/* <div className="flex bg-white rounded-lg p-4 items-center justify-between"
                         onClick={() => navigate(TaskPath.DELIVERY_TASK_TRANSFER_OUT)}>
                        <div className="w-1/5 text-4xl font-bold text-center">{taskInfo?.transferOutCount}</div>
                        <div className="felx-1 flex-col w-2/5 h-full gap-2">
                            <div
                                className="font-medium text-lg text-black">{t('pages.delivery.task.transferOutTitle')}</div>
                            <span>{t('pages.delivery.task.transferOutDesc')}</span>
                            <div className="flex">
                                <span className="mr-1 text-violet-600">{t('pages.view')}</span>
                                <RightOutlined color="#1677ff"/>
                            </div>
                        </div>
                        <div className="w-2/5 h-full p-6">
                            <TransferOutIcon className="w-full h-full"/>
                        </div>
                    </div> */}

                </div>
              </PullToRefreshWrapper>
            </div>
        </DeliveryLayout>
    );
};

export default TaskPage;